<script setup lang="ts">
definePageMeta({
  layout: 'bare',
  pageTemplate: 'home',
  pageType: 'home',
});
const { t, locale } = useI18n();
const i18nHead = useLocaleHead({ key: 'id' });
useHead({
  title: t('homepage.seo.title'),
  htmlAttrs: {
    lang: i18nHead.value.htmlAttrs!.lang,
  },
  // Follow Nuxt I18N documentation
  // https://i18n.nuxtjs.org/docs/guide/seo#feature-details
  link: [...(i18nHead.value?.link ?? [])],
  meta: [
    { name: 'description', content: t('homepage.seo.meta_desc') },
    { property: 'og:title', content: t('homepage.seo.title') },
    { property: 'og:description', content: t('homepage.seo.meta_desc') },
    { property: 'og:type', content: 'website' },
    { property: 'og:locale', content: locale.value },
    { name: 'twitter:site', content: '@IADFrance' },
    { name: 'twitter:card', content: 'summary' },
    { name: 'twitter:title', content: t('homepage.seo.title') },
    { name: 'twitter:description', content: t('homepage.seo.meta_desc') },
    { name: 'robots', content: 'index, follow' },
    ...(i18nHead.value?.meta ?? []),
  ],
});

const { data: homepageData, suspense } = useHomepageProperties();
await suspense();

const latestProperties = computed(() => homepageData.value?.latestProperties);
const latestTransactions = computed(() => homepageData.value?.latestTransactions);

const hasLatestProperties = computed(() => {
  return homepageData.value?.latestProperties.sale.length
    || homepageData.value?.latestProperties.rental.length;
});

const hasSoldProperties = computed(() => {
  return homepageData.value?.latestTransactions.sold.length
    || homepageData.value?.latestTransactions.rented.length;
});

const hasFrenchLocale = computed(() => locale.value === 'fr');
const blogArticles = computed(() => homepageData.value?.blogArticles);
const hasBlogArticles = computed(() => {
  return blogArticles.value?.length && hasFrenchLocale.value;
});
</script>

<template>
  <main>
    <MainHeader variant="homepage" />
    <HomeCover />
    <HomeSearchForm class="mt-[-110px]" />
    <HomeLatestPropertyList
      v-if="latestProperties && hasLatestProperties"
      :latest-properties
    />
    <HomeReviews
      v-if="homepageData?.reviews"
      class="py-xl lg:py-2xl"
      :reviews="homepageData.reviews"
    />
    <HomeAgent />
    <HomePromoRugby v-if="hasFrenchLocale" />
    <HomeSoldPropertyList
      v-if="latestTransactions && hasSoldProperties"
      :latest-transactions
    />
    <HomeBlogSection
      v-if="blogArticles && hasBlogArticles"
      :blog-articles
    />
    <HomePromoIad />
    <IadWrapper
      class="pt-2xl pb-s"
      tag="div"
    >
      <I18nT
        keypath="homepage.legal_notice"
        class="whitespace-pre-line text-neutral-40"
        tag="p"
      >
        <template #immodvisor>
          <a
            href="https://www.immodvisor.com/pro/iad-france-lieusaint-77127-15686.html"
            class="underline"
            target="_blank"
            v-text="'www.immodvisor.com/pro/iad-france-lieusaint-77127-15686.html'"
          />
        </template>
      </I18nT>
    </IadWrapper>
    <MainFooter />
    <FindAgentStickyCta />
  </main>
</template>
